import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, User, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLogin() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Check if input looks like an email (for DB users) or username (for master admin)
      const isEmail = username.includes('@');
      
      let response;
      if (isEmail) {
        // Try DB user login
        response = await axios.post(`${API}/admin/users/login`, {
          email: username,
          password,
        });
        localStorage.setItem('admin_token', response.data.token);
        localStorage.setItem('admin_username', response.data.user.nombre);
        localStorage.setItem('admin_user_data', JSON.stringify(response.data.user));
      } else {
        // Try master admin login
        response = await axios.post(`${API}/admin/login`, {
          username,
          password,
        });
        localStorage.setItem('admin_token', response.data.token);
        localStorage.setItem('admin_username', response.data.username);
      }

      toast.success('¡Bienvenido!');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Credenciales inválidas');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-8">
            <img
              src="/amplicare-logo.svg"
              alt="Amplicare Lab"
              className="h-12 mb-6"
            />
            <h1 className="text-3xl font-heading font-bold text-slate-900 mb-2">
              Área de Administración
            </h1>
            <p className="text-slate-600">
              Acceda al panel de control para gestionar solicitudes
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="font-medium text-slate-700">
                Usuario o Email
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="username"
                  data-testid="username-input"
                  type="text"
                  placeholder="admin o correo@ejemplo.com"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="h-12 pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="font-medium text-slate-700">
                Contraseña
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pl-10"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              data-testid="login-button"
              className="w-full h-12 bg-primary-amplicare hover:bg-primary-amplicare-dark text-white"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Iniciando sesión...
                </>
              ) : (
                'Iniciar Sesión'
              )}
            </Button>
          </form>

          {/* Back to Home */}
          <div className="mt-6 text-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-slate-600"
              data-testid="back-to-home-button"
            >
              Volver al inicio
            </Button>
          </div>
        </div>
      </div>

      {/* Right Side - Image */}
      <div
        className="hidden lg:block lg:w-1/2 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/6234626/pexels-photo-6234626.jpeg)',
        }}
      >
        <div className="w-full h-full bg-primary-amplicare bg-opacity-20 backdrop-blur-sm flex items-center justify-center">
          <div className="text-center text-white p-8">
            <h2 className="text-4xl font-heading font-bold mb-4">
              Gestión de Solicitudes
            </h2>
            <p className="text-xl opacity-90">
              Control total de las pruebas de laboratorio
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}